/**
 * Onboarding Wizard page (client-side only).
 *
 * A custom-chrome wizard (StepIndicator + footer) with six steps:
 *   1. Connect & import schema — collects the database connection (cluster,
 *      database, and the Test/Beta/Live secret ARNs) AND imports the table
 *      schema. As soon as columns are available (DDL/CSV/JSON parse OR Redshift
 *      introspection) it loads the columns and runs auto-inference (inferConfig)
 *      to pre-fill the rest of the wizard. Advancing is the footer
 *      "Next"'s job, not this step's.
 *   2. Tool Metadata     — name / type / category.
 *   3. Metrics           — editable base + derived metric tables.
 *   4. Parameters        — editable input_schema parameter table.
 *   5. Description       — the LLM-authored (Spec 6) tool description block.
 *   6. Review & Generate — read-only preview of the assembled YAML tool config,
 *      with download / copy (client-side only; no upload — Finish just logs).
 *
 * State flow:
 *   columns available -> dispatch LOAD_COLUMNS (sets sourceTableName) ->
 *   inferConfig(columns) -> dispatch LOAD_INFERENCE (pre-fills
 *   toolName/category/baseMetrics) -> user clicks the single footer
 *   "Next" -> validateStep(0) passes (schema imported + DB config valid) ->
 *   advance -> user edits steps 2-5 -> validateStep gates each Next.
 *
 * No network, no AWS, no backend. The lead wires the route separately.
 */

import React, { useReducer, useState, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  ContentLayout,
  Header,
  SpaceBetween,
} from '@amzn/awsui-components-react';

import { CONFIG_GENERATOR_ENDPOINT } from 'src/constants';

import StepIndicator from './StepIndicator';
import SchemaImportStep from './steps/SchemaImportStep';
import ToolMetadataStep from './steps/ToolMetadataStep';
import MetricsStep from './steps/MetricsStep';
import ParametersStep from './steps/ParametersStep';
import DescriptionStep from './steps/DescriptionStep';
import YamlPreviewStep from './steps/YamlPreviewStep';

import { onboardingReducer, initialOnboardingState } from './onboardingReducer';
import { validateStep, isStepValid } from './onboardingValidators';
import { inferConfig } from './autoInference';
import { heuristicAdvisor } from './metricAdvisor';
import { createLlmMetricAdvisor } from './llmMetricAdvisor';
import { heuristicParameterAdvisor } from './parameterAdvisor';
import { createLlmParameterAdvisor } from './llmParameterAdvisor';
import { heuristicDescriptionAdvisor, createLlmDescriptionAdvisor } from './llmDescriptionAdvisor';
import { ColumnsResponse } from './types';

/**
 * The service endpoint is templated at deploy time. When it's still the
 * unsubstituted placeholder (local/dev with no backend), there's nothing for
 * the LLM advisor to call, so fall back to the client-side heuristic.
 *
 * NOTE: detect via `.startsWith('{{')` rather than an equality check against
 * the full `'https://alpha.management.bluey.device.finance.amazon.dev'` literal. The KatalWebpackPlugin literal-
 * replaces every occurrence of `https://alpha.management.bluey.device.finance.amazon.dev` in the bundle at deploy
 * time, including a comparison literal — that would make the check
 * `'https://...' !== 'https://...'` (always false) in deployed builds. The
 * `'{{'` prefix is not a full token the plugin matches, so it survives
 * substitution (same pattern as `isArnConfigured` in constants.ts).
 */
const BACKEND_AVAILABLE = !CONFIG_GENERATOR_ENDPOINT.startsWith('{{');

const OnboardingWizardPage: React.FC = () => {
  const [state, dispatch] = useReducer(onboardingReducer, initialOnboardingState);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [stepError, setStepError] = useState<string | null>(null);

  // ── Metric advisor for the Metrics step ───────────────────────────
  // Use the real LLM advisor (Spec 2) when a backend endpoint is configured,
  // bound to the current tool/table context. Fall back to the client-side
  // heuristic in local/dev where there's no backend to call. Recomputed when
  // the bound context changes so suggestions reflect the latest tool metadata.
  const { toolName, category } = state.config;
  const schema = state.columns?.schema ?? null;
  const tableName = state.columns?.tableName ?? '';
  const advisor = useMemo(
    () =>
      BACKEND_AVAILABLE
        ? createLlmMetricAdvisor({ toolName, category, schema, tableName })
        : heuristicAdvisor,
    [toolName, category, schema, tableName],
  );

  // ── Parameter advisor for the Parameters step ─────────────────────
  // Same swap as `advisor`: the real LLM advisor (Spec 3) when a backend
  // endpoint is configured (bound to the tool/table context AND the confirmed
  // metric set the user just edited on the Metrics step), else the client-side
  // heuristic. supportsPeriodComparison is always true for v1.
  const { baseMetrics, derivedMetrics } = state.config;
  const parameterAdvisor = useMemo(
    () =>
      BACKEND_AVAILABLE
        ? createLlmParameterAdvisor({
            toolName,
            category,
            schema,
            tableName,
            confirmedBaseMetrics: baseMetrics,
            confirmedDerivedMetrics: derivedMetrics,
            supportsPeriodComparison: true,
          })
        : heuristicParameterAdvisor,
    [toolName, category, schema, tableName, baseMetrics, derivedMetrics],
  );

  // ── Description advisor for the Description step (Spec 6) ──────────
  // Same swap: the real LLM advisor when a backend endpoint is configured
  // (bound to the tool metadata + the confirmed metrics; the confirmed
  // parameters are read from state.config.parameters at suggest() time via the
  // step's input), else the client-side heuristic. supportsPeriodComparison is
  // always true for v1. Recomputed when the bound context changes; the deps
  // include state.config.parameters so a re-suggest reflects the latest params.
  const parameters = state.config.parameters;
  const descriptionAdvisor = useMemo(
    () =>
      BACKEND_AVAILABLE
        ? createLlmDescriptionAdvisor({
            toolName,
            category,
            supportsPeriodComparison: true,
          })
        : heuristicDescriptionAdvisor,
    [toolName, category, baseMetrics, derivedMetrics, parameters],
  );

  // ── Schema import -> auto-inference pre-fill ───────────────────────
  // Fired as soon as columns are available (parse success OR Redshift fetch).
  // Loads columns (which sets sourceTableName) and runs best-effort inference.
  // It does NOT advance the wizard — the single footer "Next" handles that, and
  // it now validates because the reducer has columns. Clearing the step error
  // here removes any stale "import a schema first" message from a prior attempt.
  const handleColumnsLoaded = useCallback((columns: ColumnsResponse) => {
    dispatch({ type: 'LOAD_COLUMNS', columns });
    try {
      const inference = inferConfig(columns);
      dispatch({ type: 'LOAD_INFERENCE', inference });
    } catch {
      // Inference is best-effort pre-fill; if it throws the user can still
      // fill the remaining steps by hand. Columns are already loaded.
    }
    setStepError(null);
  }, []);

  // ── Step navigation with per-step validation ──────────────────────
  // Extracted from the old Wizard `onNavigate` handler: backward navigation is
  // always free (clears any error); forward navigation is gated on the current
  // step's validity. Drives both the top StepIndicator clicks and the footer
  // Previous/Next buttons.
  const goToStep = useCallback(
    (requestedStepIndex: number) => {
      // Allow free backward navigation.
      if (requestedStepIndex < activeStepIndex) {
        setStepError(null);
        setActiveStepIndex(requestedStepIndex);
        return;
      }
      if (requestedStepIndex === activeStepIndex) {
        return;
      }
      // Gate forward navigation on the current step's validity.
      const error = validateStep(activeStepIndex, state.config);
      if (error) {
        setStepError(error);
        return;
      }
      setStepError(null);
      setActiveStepIndex(requestedStepIndex);
    },
    [activeStepIndex, state.config],
  );

  const handleSubmit = useCallback(() => {
    const error = validateStep(activeStepIndex, state.config);
    if (error) {
      setStepError(error);
      return;
    }
    setStepError(null);
    // Week 4 is client-side only: no upload. Surface the assembled config for
    // the next step's owner (YAML generation / upload lands later).
    // eslint-disable-next-line no-console
    console.log('Onboarding config ready:', state.config);
  }, [activeStepIndex, state.config]);

  const errorSlot = stepError ? <Alert type="error">{stepError}</Alert> : null;

  const steps: { title: string; description?: string; content: React.ReactNode }[] = [
    {
      title: 'Connect & import schema',
      description:
        'Set the database connection and secret ARNs, then paste DDL/CSV/JSON or connect to Redshift to detect the table columns.',
      content: (
        <>
          {errorSlot}
          <SchemaImportStep
            onColumnsLoaded={handleColumnsLoaded}
            initialColumns={state.columns}
            businessContext={state.config.businessContext}
            onBusinessContextChange={(value) =>
              dispatch({ type: 'SET_BUSINESS_CONTEXT', value })
            }
            columnNotes={state.config.columnNotes}
            onColumnNoteChange={(columnName, note) =>
              dispatch({ type: 'SET_COLUMN_NOTE', columnName, note })
            }
            config={state.config}
            dispatch={dispatch}
          />
        </>
      ),
    },
    {
      title: 'Tool metadata',
      content: (
        <>
          {errorSlot}
          <ToolMetadataStep config={state.config} dispatch={dispatch} backendAvailable={BACKEND_AVAILABLE} />
        </>
      ),
    },
    {
      title: 'Metrics',
      content: (
        <>
          {errorSlot}
          <MetricsStep
            config={state.config}
            columns={state.columns?.columns}
            dispatch={dispatch}
            advisor={advisor}
          />
        </>
      ),
    },
    {
      title: 'Parameters',
      content: (
        <>
          {errorSlot}
          <ParametersStep
            config={state.config}
            columns={state.columns?.columns}
            dispatch={dispatch}
            advisor={parameterAdvisor}
          />
        </>
      ),
    },
    {
      title: 'Description',
      content: (
        <>
          {errorSlot}
          <DescriptionStep
            config={state.config}
            columns={state.columns?.columns}
            dispatch={dispatch}
            advisor={descriptionAdvisor}
          />
        </>
      ),
    },
    {
      title: 'Review & Generate',
      description:
        'Review the assembled YAML tool config, then create the metric catalog tables Bluey resolves metrics against.',
      content: (
        <>
          {errorSlot}
          <YamlPreviewStep
            config={state.config}
            dispatch={dispatch}
            backendAvailable={BACKEND_AVAILABLE}
          />
        </>
      ),
    },
  ];

  // ── Completion / gating for the top step indicator ────────────────
  // A step is reachable (clickable) when every step before it is valid; the
  // user may also always click back to any already-reached step. So the
  // furthest clickable index is the greater of: the current step, and the first
  // step that fails validation (which the user is allowed to land on to fix).
  const firstInvalidIndex = useMemo(() => {
    for (let i = 0; i < steps.length; i++) {
      if (!isStepValid(i, state.config)) {
        return i;
      }
    }
    return steps.length - 1;
  }, [steps.length, state.config]);
  const furthestValidatedIndex = Math.max(activeStepIndex, firstInvalidIndex);

  const isLastStep = activeStepIndex === steps.length - 1;

  return (
    <ContentLayout header={<Header variant="h1">Onboarding Wizard</Header>}>
      <SpaceBetween size="l">
        <StepIndicator
          steps={steps}
          activeStepIndex={activeStepIndex}
          furthestValidatedIndex={furthestValidatedIndex}
          onStepClick={goToStep}
        />
        {steps[activeStepIndex].description && (
          <Box variant="p">{steps[activeStepIndex].description}</Box>
        )}
        <Box>{steps[activeStepIndex].content}</Box>
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            {activeStepIndex > 0 && (
              <Button onClick={() => goToStep(activeStepIndex - 1)}>Previous</Button>
            )}
            {isLastStep ? (
              <Button variant="primary" onClick={handleSubmit}>
                Finish
              </Button>
            ) : (
              <Button variant="primary" onClick={() => goToStep(activeStepIndex + 1)}>
                Next
              </Button>
            )}
          </SpaceBetween>
        </Box>
      </SpaceBetween>
    </ContentLayout>
  );
};

export default OnboardingWizardPage;
